import React from 'react'
import Image from 'next/image'
import styles from './index.module.scss'
import Icon from 'common/Icon'
import { GRASS_GREEN, NAVY } from 'constants/colors'
import Button from 'component_library/1_Atoms/Button'
import logEvent from 'utils/logEvent'
import { RudderEvent } from 'constants/rudderEvents'
import {
  GTMClasses,
  IN_NETWORK_COVERAGE_FOOTNOTE,
  providerTypes,
  SeFriendlyHubPageTitle,
  SeFriendlyPageType,
  seFriendlyPhoneNumber,
} from 'app/SeFriendly/constants'
import { getHeroSubContentTextItems } from '../helpers'
import useBreakpoint from 'hooks/useBreakPoint'
import classNames from 'classnames'
import { thriveworksLinks } from 'constants/footer'
import usePartnershipCodeUrl from 'hooks/usePartnershipCodeUrl'

type HeroLayoutComponentProps = {
  hubPageType: string
}

const HubPageHeroSection: React.FC<HeroLayoutComponentProps> = ({
  hubPageType,
}) => {
  const { phoneNumber, formattedPhoneNumber } = seFriendlyPhoneNumber
  const { isMobileScreen } = useBreakpoint()
  const addPartnershipCodeToUrl = usePartnershipCodeUrl()
  const isSeFriendlyPsychiatristsHubPage =
    hubPageType === providerTypes.PSYCHIATRISTS

  const handleSeFriendlyPhoneCall = () => {
    window.location.href = `tel:${phoneNumber}`

    logEvent(RudderEvent.PHONE_NUMBER_CLICKED, {
      phone_number: formattedPhoneNumber,
    })
  }

  const heroSubContentTextItems = getHeroSubContentTextItems(
    SeFriendlyPageType.HUB,
    hubPageType
  )

  const aboutUsPageLink = thriveworksLinks.find(
    ({ label }) => label === 'About us'
  )?.link

  const aboutUsHref = addPartnershipCodeToUrl(aboutUsPageLink)

  return (
    <>
      <div className={styles.HubPageHeroSection}>
        <div className={styles.HubPageHeroSection__background} />
        <div className={styles.HubPageHeroSection__container}>
          <div className={styles.HubPageHeroSection__bannerSection}>
            <div className={styles.HubPageHeroSection__heroContentWrapper}>
              <div className={styles.HubPageHeroSection__heroContent}>
                <h1 className={styles.HubPageHeroSection__headingText}>
                  {isSeFriendlyPsychiatristsHubPage
                    ? SeFriendlyHubPageTitle.PSYCHIATRY
                    : SeFriendlyHubPageTitle.THERAPY}
                </h1>
                <div>
                  {heroSubContentTextItems.map((subContentItemText, index) => (
                    <div
                      key={`${subContentItemText}-${index}`}
                      className={styles.HubPageHeroSection__heroSubContentItem}
                    >
                      <div>
                        <Icon
                          icon="check"
                          fill={GRASS_GREEN}
                          className={
                            styles.HubPageHeroSection__heroSubContentItem__checkMarkIcon
                          }
                        />
                      </div>
                      <div
                        className={
                          styles.HubPageHeroSection__heroSubContentItem__text
                        }
                      >
                        {subContentItemText}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.HubPageHeroSection__ButtonContainer}>
                  <Button
                    variant="quinary"
                    theme="v2"
                    className={classNames(
                      GTMClasses.CTA_HERO_SECTION_CALL_PHONE_NUMBER,
                      styles.HubPageHeroSection__Button
                    )}
                    onClick={handleSeFriendlyPhoneCall}
                  >
                    Call {formattedPhoneNumber}
                  </Button>
                </div>
              </div>
            </div>
            {!isMobileScreen && (
              <div
                className={classNames(
                  styles.HubPageHeroSection__heroImageRightContainer,
                  {
                    [styles.HubPageHeroSection__heroImageRightPsychiatrist]: isSeFriendlyPsychiatristsHubPage,
                  }
                )}
              />
            )}
          </div>
          <p className={styles.HubPageHeroSection__inNetworkCoverageFootnote}>
            {IN_NETWORK_COVERAGE_FOOTNOTE}
          </p>
        </div>
      </div>
      <div className={styles.HubPageHeroSection__hubPageFooter}>
        <div className={styles.HubPageHeroSection__hubPageFooterLogo}>
          <Image
            src="/images/heroLayoutHubFooterLogo.svg"
            alt="Hero section hub page footer thriveworks logo"
            width={37}
            height={37}
          />
        </div>
        <div className={styles.HubPageHeroSection__hubPageFooterContent}>
          <div className={styles.HubPageHeroSection__hubPageFooterText}>
            Thriveworks is an industry-leading provider of therapy and
            psychiatry.
          </div>
          <a target="_blank" rel="noreferrer" href={aboutUsHref}>
            <Button
              theme="v2"
              variant="tertiary"
              className={styles.HubPageHeroSection__hubPageFooterLearnMoreCta}
              aria-label="Hero section learn more cta"
            >
              Learn more
              <Icon
                icon="rightArrow"
                fill={NAVY}
                className={styles.HubPageHeroSection__learnMoreCtaArrowIcon}
              />
            </Button>
          </a>
        </div>
      </div>
    </>
  )
}

export default HubPageHeroSection
